import React, { useState, useEffect } from 'react';
import Lottie from 'react-lottie';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import fireAnimationData from '/src/animations/fire-animation.json';
import clockAnimationData from '/src/animations/clock-animation.json';
import { createCheckoutSession } from '../../services/stripe';
import WaitlistButton from '../WaitlistButton';

// Animation options
const fireOptions = {
  loop: true,
  autoplay: true,
  animationData: fireAnimationData,
  rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
};

const clockOptions = {
  loop: true,
  autoplay: true,
  animationData: clockAnimationData,
  rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
};

const translations = {
  fr: {
    limitMessage: "Places limitées à 300 utilisateurs par mois pour des raisons techniques. Inscrivez-vous à la liste d'attente pour être parmi les premiers.",
    privacyMessage: "Aucun contenu promotionnel, aucun partage de données. Utilisez l'email de votre choix.",
    limitedOffer: "Offre limitée",
    untilEndOfMonth: "Jusqu'à la fin du mois",
    remaining: "restants",
    availablePlaces: "places disponibles"
  },
  en: {
    limitMessage: "Limited to 300 users per month due to technical constraints. Join the waitlist to be among the first.",
    privacyMessage: "No promotional content, no data sharing. Use any email address.",
    limitedOffer: "Limited offer",
    untilEndOfMonth: "Until the end of the month",
    remaining: "remaining",
    availablePlaces: "available spots"
  }
};

// Use translations based on current language
const currentLang = 'fr'; // You can make this dynamic based on your i18n setup
const messages = translations[currentLang];

const PricingMyPlum = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  const [spotsLeftMyPlum, setSpotsLeftMyPlum] = useState(0);
  const [spotsLeftMyPlumPro, setSpotsLeftMyPlumPro] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showStandardForm, setShowStandardForm] = useState(false);
  const [showProForm, setShowProForm] = useState(false);
  const [standardSubmitted, setStandardSubmitted] = useState(false);
  const [proSubmitted, setProSubmitted] = useState(false);
  const [isSubmittingStandard, setIsSubmittingStandard] = useState(false);
  const [isSubmittingPro, setIsSubmittingPro] = useState(false);
  const [showFaq, setShowFaq] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  const [standardFormData, setStandardFormData] = useState({
    prenom: '',
    email: '',
  });
  const [proFormData, setProFormData] = useState({
    prenom: '',
    email: '',
  });
  const projectSuspended = true; // Added project suspension status flag

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  function calculateTimeLeft() {
    const now = new Date();
    // Fin du mois en cours : (jour 0 du mois suivant => dernier jour du mois actuel)
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    const difference = endOfMonth.getTime() - now.getTime();

    return difference > 0
      ? {
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / (1000 * 60)) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        }
      : { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  // Ouvre la page de paiement Stripe dans un nouvel onglet
  const handleStripePayment = async (planType) => {
    setIsLoading(true);
    try {
      console.log('Processing payment for:', planType);
      // Add user email if available (this could be from a form or authentication)
      const userEmail = ''; // Optional, can be left empty
      await createCheckoutSession(planType, userEmail);
    } catch (error) {
      console.error('Payment error:', error);
      // More informative error message
      alert(`Payment error: ${error.message || 'An error occurred. Please try again.'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowStandardForm = () => {
    setShowStandardForm(!showStandardForm);
  };

  const handleShowProForm = () => {
    setShowProForm(!showProForm);
  };

  const handleStandardFormChange = (e) => {
    const { name, value } = e.target;
    setStandardFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProFormChange = (e) => {
    const { name, value } = e.target;
    setProFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStandardFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingStandard(true);
    
    try {
      const url = window.location.href;
      const userAgent = navigator.userAgent;
      
      const response = await fetch('/api/send-waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...standardFormData,
          planType: 'myplum-standard',
          url,
          userAgent
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setStandardSubmitted(true);
        console.log('Standard waitlist registration successful:', data);
      } else {
        console.error('Standard waitlist registration failed:', data);
        alert('Une erreur est survenue. Veuillez réessayer.');
      }
    } catch (error) {
      console.error('Error submitting standard waitlist form:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmittingStandard(false);
    }
  };

  const handleProFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingPro(true);
    
    try {
      // Get the current URL and user agent
      const url = window.location.href;
      const userAgent = navigator.userAgent;
      
      // Send data to the waitlist API
      const response = await fetch('/api/send-waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...proFormData,
          planType: 'myplum-pro',
          url,
          userAgent
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setProSubmitted(true);
        console.log('Waitlist registration successful:', data);
      } else {
        console.error('Waitlist registration failed:', data);
        alert('Une erreur est survenue. Veuillez réessayer.');
      }
    } catch (error) {
      console.error('Error submitting waitlist form:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmittingPro(false);
    }
  };

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  // Direct access to dashboard function
  const handleFreeAccess = (e) => {
    if (projectSuspended) {
      e.preventDefault();
      return;
    }
    navigate('/dashboard');
  };

  // Calcul de la progression de la promo dans le mois en cours (pour myPlum+)
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  const totalMonthDuration = endOfMonth.getTime() - startOfMonth.getTime();
  const elapsedMonthDuration = now.getTime() - startOfMonth.getTime();
  const promoProgress = Math.round((elapsedMonthDuration / totalMonthDuration) * 100);

  // Calcul pour les places myPlum+ standard
  const totalMyPlumPlaces = 100;
  const takenMyPlumPlaces = totalMyPlumPlaces - spotsLeftMyPlum; // Will be 100 since spotsLeft is 0
  const myPlumPercentage = Math.round((takenMyPlumPlaces / totalMyPlumPlaces) * 100); // Will be 100%

  // Calcul pour la formule PRO (places limitées)
  const totalProPlaces = 50;
  const takenProPlaces = totalProPlaces - spotsLeftMyPlumPro; // Will be 50 since spotsLeft is 0
  const proPercentage = Math.round((takenProPlaces / totalProPlaces) * 100); // Will be 100%

  // Features for each plan - With checkmarks instead of icons
  const plusFeatures = [
    { 
      text: 'Guides complets : divorce, garde d\'enfants, pension alimentaire et séparation de fait',
      new: false 
    },
    { 
      text: 'Outil d\'estimation des frais de justice',
      new: true 
    },
    { 
      text: 'Plan des étapes pas à pas avec estimation des délais',
      new: false 
    },
    { 
      text: 'Conseils pour réduire vos coûts d\'avocat',
      new: true 
    },
    { 
      text: 'Orientation vers l\'aide juridique et autres ressources',
      new: false 
    },
   
    
  ];
  
  const proFeatures = [
    { 
      text: 'Tous les avantages de la formule myPlum+ inclus',
      new: false 
    },
    { 
      text: 'Préparation complète de votre dossier juridique (50 pages)',
      new: false 
    },
    { 
      text: 'Aide à la formulation de vos documents en langage juridique',
      new: true 
    },
    { 
      text: 'Informations sur les démarches d\'assermentation et de certification de documents',
      new: true 
    },
    
    { 
      text: 'Démarches personnalisées pour votre auto-représentation',
      new: false 
    },
    { 
      text: 'Guide prioritaire 24h/24 par téléphone et email',
      new: false 
    },
  ];

  // FAQ items
  const faqItems = [
    {
      question: "Quelle est la différence entre les deux formules?",
      answer: "La formule standard vous offre des outils et guides pour vous aider à mieux comprendre et naviguer votre séparation. En phase de développement, l\'accès aux ressources est gratuit mais deviendra payant pour des fonctionnalités supplémentaires. Notez qu\'il n\'y a pas d\'accompagnement 1-on-1 - ce sont les outils du site qui vous guident dans vos démarches."
    },
    {
      question: "Comment fonctionne la garantie?",
      answer: "Si vous n'êtes pas satisfait dans les 7 jours, nous vous remboursons intégralement, sans condition."
    },
    {
      question: "Quelle est la durée d'accès?",
      answer: "L'accès est illimité dans le temps. Une fois acheté, le contenu reste disponible en permanence."
    },
    {
      question: "Le paiement est-il sécurisé?",
      answer: "Oui, nous utilisons Stripe, le leader mondial du paiement sécurisé en ligne."
    }
  ];

  return (
    <section
      id="tarifs"
      className="py-8 md:py-16 bg-gradient-to-b from-gray-50 to-white text-gray-900 relative overflow-hidden"
    >
      {/* Keep the style tag for animations and complex styles that can't be easily done with Tailwind */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.6; }
          100% { opacity: 1; }
        }
        @keyframes scalePulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
        .waitlist-form-enter {
          max-height: 0;
          opacity: 0;
          transform: translateY(-10px);
        }
        .waitlist-form-enter-active {
          max-height: 300px;
          opacity: 1;
          transform: translateY(0);
          transition: all 300ms ease-in-out;
        }
        .waitlist-form-exit {
          max-height: 300px;
          opacity: 1;
          transform: translateY(0);
        }
        .waitlist-form-exit-active {
          max-height: 0;
          opacity: 0;
          transform: translateY(-10px);
          transition: all 300ms ease-in-out;
        }
      `}</style>

      <div className="container mx-auto px-3 sm:px-6 max-w-5xl relative z-10">
        {/* Information card with translations */}
        <div className="max-w-3xl mx-auto mb-12">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
            <div className="relative">
              {/* Top gradient bar */}
              <div className="h-2 bg-gradient-to-r from-primary via-secondary to-primary"></div>
              
              {/* Project suspension notice */}
              {projectSuspended && (
                <div className="bg-error text-white p-4 font-medium">
                  <div className="flex items-center justify-center mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span className="text-lg font-bold">Projet Suspendu</span>
                  </div>
                  <p className="text-center">Ce projet est actuellement suspendu jusqu'à nouvel ordre. Merci de votre compréhension.</p>
                </div>
              )}
              
              {/* Main content */}
              <div className="px-6 py-6 md:py-8">
                <div className="text-center space-y-4">
                  <div className="text-gray-800 text-lg">
                    {currentLang === 'fr' 
                      ? "Places limitées à 300 utilisateurs par mois pour des raisons techniques. Inscrivez-vous à la liste d'attente pour être parmi les premiers."
                      : "Limited to 300 users per month due to technical constraints. Join the waitlist to be among the first."
                    }
                  </div>
                  <div className="text-gray-600 text-sm">
                    {currentLang === 'fr'
                      ? "Aucun contenu promotionnel, aucun partage de données. Utilisez l'email de votre choix."
                      : "No promotional content, no data sharing. Use any email address."
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* More compact header */}
        <header className="text-center mb-8 sm:mb-12 px-2">
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-4 leading-tight">
            <span className="bg-gradient-to-r from-primary to-secondary text-transparent bg-clip-text">
              Accès à nos outils juridiques - 100% Gratuit
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Tous nos outils sont disponibles gratuitement pour vous aider dans vos démarches juridiques.
          </p>
        </header>

        {/* Redesigned more compact pricing cards */}
        <section className="grid grid-cols-1 md:grid-cols-1 gap-4 sm:gap-6 max-w-5xl mx-auto relative items-start">
          {/* Carte myPlum+ with blue color scheme restored */}
          <article
            className="relative bg-white border-2 border-amber-500 rounded-xl overflow-hidden flex flex-col transition-all duration-300 shadow-md hover:shadow-xl hover:-translate-y-1 w-full max-w-lg mx-auto backdrop-blur-md"
          >
            {/* Popular badge redesigned */}
            <div className="absolute top-0 right-0 z-10">
              <div className="bg-amber-500 text-white py-1 px-3 text-xs font-semibold transform rotate-45 translate-x-[30%] translate-y-[10%]">
                {t('pricing.standard.badge')}
              </div>
            </div>
            
            <div className="p-4 sm:p-6">              
              <h3 className="text-xl sm:text-2xl font-bold mb-2 flex items-center">
                <span className="px-3 py-1 bg-primary text-white rounded-lg inline-block relative">
                  {t('pricing.standard.title')}
                </span>
                
                {/* Improved scarcity badge with consistent text size */}
                <span className="ml-auto flex items-center bg-gradient-to-r from-amber-400 to-orange-400 text-white px-2.5 py-1.5 rounded-full text-xs font-medium shadow-sm whitespace-nowrap">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 mr-1.5">
                    <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 000-1.5h-3.75V6z" clipRule="evenodd" />
                  </svg>
                  <span className="inline-flex items-center">
                    {spotsLeftMyPlum} {t('pricing.standard.progress.available')}
                  </span>
                </span>
              </h3>
              
              <hr className="border-gray-200 my-3" />

              {/* Beta banner - Updated to match consistent design */}
              <div className="relative mb-4 overflow-hidden rounded-xl shadow-sm">
                <div className="absolute -left-12 top-3 w-40 bg-amber-500 text-white py-1 text-xs font-bold text-center transform -rotate-45 shadow-md">
                  β BETA
                </div>
                <div className="pt-1 pb-3 px-4 border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-amber-100 rounded-xl">
                  <p className="text-xs text-amber-800 mt-3 ml-6">
                    L'essentiel des informations est présenté, bien que certains détails puissent être absents. Nous travaillons à trouver des façons innovantes de présenter les informations supplémentaires si nécessaire.
                  </p>
                </div>
              </div>
              
              {/* Price description */}
              <div className="mb-3 text-center">
                <div className="flex items-end justify-center">
                  <span className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-amber-400 to-orange-600 bg-clip-text text-transparent pr-1">
                    Gratuit
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1 flex justify-center items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Accès illimité
                </p>
              </div>

              {/* More compact features list */}
              <ul className="space-y-1.5 flex-grow mb-3">
                {t('pricing.standard.features', { returnObjects: true }).map((feature, index) => (
                  <li key={index} className="flex items-start py-1 px-1 rounded-md hover:bg-gray-50 transition-colors">
                    <svg className="h-4 w-4 text-blue-500 mr-1.5 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-xs sm:text-sm">
                      {feature.text}
                      {feature.new && (
                        <span className="ml-1 inline-block bg-green-100 text-green-800 text-[0.6rem] px-1 py-px rounded-sm animate-pulse">
                          {t('common.new')}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Progress bar with Tailwind - IMPROVED SPACING AND VISUAL */}
              <div className="my-6 bg-gray-100 rounded-full h-4 p-0.5 mx-1">
                <div 
                  className="h-3 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 transition-all duration-1000"
                  style={{width: `${myPlumPercentage}%`}}
                >
                </div>
                <div className="flex justify-between mt-2 px-1 text-[0.65rem] text-gray-500">
                  <span>{myPlumPercentage}% {t('pricing.standard.progress.filled')}</span>
                  <span>{spotsLeftMyPlum}/{totalMyPlumPlaces} {t('pricing.standard.progress.available')}</span>
                </div>
              </div>

              {/* Replace payment button with waitlist button */}
              <div className="flex gap-2 mt-4 mb-2">
                <button
                  onClick={handleFreeAccess}
                  className={`w-full text-sm sm:text-base font-bold py-2.5 sm:py-3 rounded-lg transition-all shadow-md ${
                    projectSuspended 
                      ? "bg-gray-500 cursor-not-allowed opacity-70 text-white" 
                      : "bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg hover:-translate-y-0.5"
                  }`}
                  disabled={projectSuspended}
                >
                  {projectSuspended ? "Accès Suspendu" : "Accéder gratuitement"}
                </button>
              </div>
              
              {/* Security text for standard plan */}
              <div className="flex flex-col sm:flex-row justify-center items-center mt-2 space-y-1 sm:space-y-0">
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-green-600 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944A11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-[0.65rem] text-gray-500">
                    Paiement sécurisé
                  </span>
                </div>
                <span className="hidden sm:inline text-gray-300 mx-1">•</span>
                <span className="text-[0.65rem] text-gray-500">
                  Contenu téléchargeable • Non remboursable
                </span>
              </div>
              
              {/* More compact waitlist form for standard plan */}
              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showStandardForm ? 'max-h-[300px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className={`mt-3 p-3 border border-gray-200 rounded-lg bg-gray-50 transform transition-all duration-300 ease-in-out ${showStandardForm ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'}`}>
                  {!standardSubmitted ? (
                    <form onSubmit={handleStandardFormSubmit} className="transition-all duration-300 ease-in-out">
                      <div className="mb-2">
                        <label className="block text-xs font-medium mb-1" htmlFor="prenom">
                          Prénom
                        </label>
                        <input
                          type="text"
                          name="prenom"
                          value={standardFormData.prenom}
                          onChange={handleStandardFormChange}
                          className="w-full p-1.5 border rounded text-xs"
                          required
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-xs font-medium mb-1" htmlFor="email">
                          Email
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={standardFormData.email}
                          onChange={handleStandardFormChange}
                          className="w-full p-1.5 border rounded text-xs"
                          required
                        />
                      </div>
                      <button
                        type="submit"
                        className="bg-amber-500 text-white py-1.5 px-3 rounded text-xs hover:bg-amber-600 transition"
                        disabled={isSubmittingStandard}
                      >
                        {isSubmittingStandard ? "Envoi en cours..." : "Confirmer"}
                      </button>
                    </form>
                  ) : (
                    <div className="text-center text-xs py-2">
                      <p className="mb-1">
                        Merci de votre inscription ! Nous vous contacterons dès le lancement.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </article>

          {/* Carte myPlum+ PRO with black color scheme restored - COMMENTED OUT */}
          {/* 
          <article
            className={`relative bg-white border-2 border-gray-800 rounded-xl overflow-hidden flex flex-col transition-all duration-300 shadow-md hover:shadow-xl hover:-translate-y-1 w-full max-w-lg mx-auto backdrop-blur-md ${showProForm ? 'min-h-[750px]' : ''}`}
          >
            <div className="p-4 sm:p-6">
              <h3 className="text-xl sm:text-2xl font-bold mb-2 flex items-center">
                <span className="px-3 py-1 bg-gray-800 text-white rounded-lg inline-block relative">
                  {t('pricing.pro.title')}
                  <span className="absolute -top-2 -right-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs px-2 py-0.5 rounded-full shadow-md">
                    {t('pricing.pro.badge')}
                  </span>
                </span>
                
                <span className="ml-auto flex items-center bg-gray-800 text-white px-2.5 py-1.5 rounded-full text-xs font-medium shadow-sm whitespace-nowrap">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 mr-1.5">
                    <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 000-1.5h-3.75V6z" clipRule="evenodd" />
                  </svg>
                  <span className="inline-flex items-center">
                    {spotsLeftMyPlumPro} {t('pricing.pro.progress.available')}
                  </span>
                </span>
              </h3>
              
              <hr className="border-gray-200 my-3" />
              
              <div className="mb-3 text-center">
                <div className="flex items-end justify-center gap-1">
                  <span className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent">
                    Gratuit
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1 flex justify-center items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Accès illimité premium
                </p>
              </div>

              <ul className="space-y-1.5 flex-grow mb-3">
                {t('pricing.pro.features', { returnObjects: true }).map((feature, index) => (
                  <li key={index} className="flex items-start py-1 px-1 rounded-md hover:bg-gray-50 transition-colors">
                    <svg className="h-4 w-4 text-gray-800 mr-1.5 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-xs sm:text-sm">
                      {feature.text}
                      {feature.new && (
                        <span className="ml-1 inline-block bg-blue-100 text-blue-800 text-[0.6rem] px-1 py-px rounded-sm animate-pulse">
                          {t('common.exclusive')}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="my-6 bg-gray-100 rounded-full h-4 p-0.5 mx-1">
                <div 
                  className="h-3 rounded-full bg-gradient-to-r from-gray-700 via-gray-800 to-gray-900 transition-all duration-1000"
                  style={{width: `${proPercentage}%`}}
                >
                </div>
                <div className="flex justify-between mt-2 px-1 text-[0.65rem] text-gray-500">
                  <span>{proPercentage}% {t('pricing.pro.progress.filled')}</span>
                  <span>{spotsLeftMyPlumPro}/{totalProPlaces} {t('pricing.pro.progress.available')}</span>
                </div>
              </div>

              <button
                onClick={handleFreeAccess}
                className="w-full text-sm mt-4 mb-2 sm:text-base font-bold py-2.5 sm:py-3 rounded-lg bg-gray-800 hover:bg-gray-900 text-white transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
              >
                Accéder gratuitement
              </button>
              
              <div className="flex flex-col sm:flex-row justify-center items-center mt-2 space-y-1 sm:space-y-0">
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-green-600 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944A11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-[0.65rem] text-gray-500">
                    Paiement sécurisé
                  </span>
                </div>
                <span className="hidden sm:inline text-gray-300 mx-1">•</span>
                <span className="text-[0.65rem] text-gray-500">
                  Contenu téléchargeable • Non remboursable
                </span>
              </div>

              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showProForm ? 'max-h-[300px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className={`mt-3 p-3 border border-gray-200 rounded-lg bg-gray-50 transform transition-all duration-300 ease-in-out ${showProForm ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'}`}>
                  {!proSubmitted ? (
                    <form onSubmit={handleProFormSubmit} className="transition-all duration-300 ease-in-out">
                      <div className="mb-2">
                        <label className="block text-xs font-medium mb-1" htmlFor="prenom">
                          {t('pricing.pro.waitlist.firstName')}
                        </label>
                        <input
                          type="text"
                          name="prenom"
                          value={proFormData.prenom}
                          onChange={handleProFormChange}
                          className="w-full p-1.5 border rounded text-xs"
                          required
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-xs font-medium mb-1" htmlFor="email">
                          {t('pricing.pro.waitlist.email')}
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={proFormData.email}
                          onChange={handleProFormChange}
                          className="w-full p-1.5 border rounded text-xs"
                          required
                        />
                      </div>
                      <button
                        type="submit"
                        className="bg-gray-800 text-white py-1.5 px-3 rounded text-xs hover:bg-gray-900 transition"
                        disabled={isSubmittingPro}
                      >
                        {isSubmittingPro ? t('pricing.pro.waitlist.sending') : t('pricing.pro.waitlist.confirm')}
                      </button>
                    </form>
                  ) : (
                    <div className="text-center text-xs py-2">
                      <p className="mb-1">
                        {t('pricing.pro.waitlist.success')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </article>
          */}
        </section>

        {/* Remove sign-up form section */}
        <div className="mt-8"></div>
      </div>
    </section>
  );
};

export default PricingMyPlum;
